import { useCallback, useEffect, useReducer } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import fetchHelper from './fetchHelper';
import consoleLogDev from './loggingHelper';
import useEventEmitter from './useEventEmitter';
import makeSelectRefreshCounter from '../selectors/refreshCounterSelector';
import { actions } from '../reducers/refreshCounter';

export const USE_RETRIEVE_DATA_EVENTS = {
    ON_FETCH_BEGIN: 'onFetchBegin',
    ON_FETCH_SUCCESS: 'onFetchSuccess',
    ON_FETCH_ERROR: 'onFetchError',
    ON_REFRESH: 'onRefresh'
};

const DATA_REDUCER_ACTIONS = {
    ON_FETCH_BEGIN: 'FETCHING',
    ON_FETCH_SUCCESS: 'FETCHED',
    ON_FETCH_ERROR: 'FETCH_ERROR'
};

const initialState = {
    isLoaded: false,
    error: null,
    data: {}
};

const DataReducer = (previousState, action) => {
    switch (action.type) {
        case DATA_REDUCER_ACTIONS.ON_FETCH_BEGIN:
            return initialState;
        case DATA_REDUCER_ACTIONS.ON_FETCH_SUCCESS:
            return { isLoaded: true, error: null, data: action.payload };
        case DATA_REDUCER_ACTIONS.ON_FETCH_ERROR:
            return { isLoaded: false, error: action.payload, data: {} };
        default:
            return previousState;
    }
}

export default function useRetrieveData(retrievalUrl, componentName) {
    //state
    const [dataState, dataDispatch] = useReducer(DataReducer, initialState);

    const dispatch = useDispatch();
    const refreshCount = useSelector(makeSelectRefreshCounter(componentName), shallowEqual);

    const applyEmitterHandler = useEventEmitter(componentName);

    const executeRetrieveData = useCallback(
        (cancelRequest) => {

            let uri = retrievalUrl;

            dataDispatch({ type: DATA_REDUCER_ACTIONS.ON_FETCH_BEGIN });

            applyEmitterHandler((emitter) => {
                emitter.emit(USE_RETRIEVE_DATA_EVENTS.ON_FETCH_BEGIN);
            });

            fetchHelper(uri)
                .then(response => response.json())
                .then(data => {
                    if (cancelRequest) return;

                    dataDispatch({ type: DATA_REDUCER_ACTIONS.ON_FETCH_SUCCESS, payload: data });

                    applyEmitterHandler((emitter) => {
                        emitter.emit(USE_RETRIEVE_DATA_EVENTS.ON_FETCH_SUCCESS, data);
                    });
                })
                .catch(err => {
                    if (cancelRequest) return;

                    dataDispatch({ type: DATA_REDUCER_ACTIONS.ON_FETCH_ERROR, payload: err });

                    applyEmitterHandler((emitter) => {
                        emitter.emit(USE_RETRIEVE_DATA_EVENTS.ON_FETCH_ERROR, err);
                    });
                });
        }, 
        [retrievalUrl, applyEmitterHandler]
    );

    //map refresh counter
    useEffect(() => {
        consoleLogDev('adding instance of refreshCounter');
        dispatch(actions.addRefreshCounter(componentName));
    }, [dispatch, componentName, applyEmitterHandler]);

    useEffect(() => {
        if (refreshCount > 0) {
            consoleLogDev(`refreshCount updated to ${refreshCount} in "useRetrieveData" for: ${componentName}`);
        }
    }, [componentName, refreshCount]);

    //trigger retrieve data from API on render
    useEffect(() => {
        let cancelRequest = false;

        executeRetrieveData(cancelRequest);

        return () => {
            cancelRequest = true;
        }
    }, [executeRetrieveData, refreshCount]);

    return dataState;
}